import {
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material'
import { HoldReaction, HoldRestrictionType } from '@planning/app/api'
import { YesNoChipBox } from '@planning/components/dangerous-goods/YesNoChipBox'
import { useTranslate } from '@tolgee/react'
import { BinIcon, FieldBox, PlusIcon, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { HoldTypeFormStore } from '../stores/HoldTypeFormStore'

interface Props {
  formStore: HoldTypeFormStore
}

export const HoldTypeRulesCard = observer(({ formStore }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  return (
    <Card>
      <CardContent>
        <Stack spacing={theme.customSpacing.m}>
          <Stack spacing={theme.customSpacing.s}>
            {formStore.restrictions.value.map((rule, index) => (
              <Grid key={index} container spacing={1} alignItems='flex-start'>
                <Grid size={6}>
                  <FormControl fullWidth error={!!rule.restrictionType.error}>
                    <InputLabel required>{t('restriction', 'Restriction')}</InputLabel>
                    <Select
                      value={rule.restrictionType.value}
                      onChange={e =>
                        rule.restrictionType.setValue(e.target.value as HoldRestrictionType)
                      }
                      label={`${t('restriction', 'Restriction')} *`}
                    >
                      {Object.values(HoldRestrictionType).map(value => (
                        <MenuItem key={value} value={value}>
                          {t(value, value)}
                        </MenuItem>
                      ))}
                    </Select>
                    {rule.restrictionType.error && (
                      <FormHelperText sx={{ ml: 0 }}>
                        {t(rule.restrictionType.error)}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={5}>
                  <FormControl fullWidth error={!!rule.reaction.error}>
                    <InputLabel required>{t('reaction', 'Reaction')}</InputLabel>
                    <Select
                      value={rule.reaction.value}
                      onChange={e => rule.reaction.setValue(e.target.value)}
                      label={`${t('reaction', 'Reaction')} *`}
                    >
                      {Object.values(HoldReaction).map(value => (
                        <MenuItem key={value} value={value}>
                          {t(value, value)}
                        </MenuItem>
                      ))}
                    </Select>
                    {rule.reaction.error && (
                      <FormHelperText sx={{ ml: 0 }}>{t(rule.reaction.error)}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={1}>
                  <Stack direction='row' alignItems='center' sx={{ minHeight: 56 }}>
                    <IconButton
                      size='small'
                      onClick={formStore.addRule}
                      aria-label={t('add', 'Add')}
                    >
                      <PlusIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => formStore.removeRule(index)}
                      aria-label={t('remove', 'Remove')}
                      sx={{ visibility: index === 0 ? 'hidden' : 'visible' }}
                    >
                      <BinIcon fontSize='small' />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            ))}
          </Stack>
          <Grid container spacing={1} alignItems='center'>
            <Grid size={6} alignItems='center'>
              <FieldBox
                label={t('activeHold', 'Active hold?')}
                rightRender={
                  <YesNoChipBox
                    activeOption={formStore.isActive.value ? 'Yes' : 'No'}
                    onClickYes={() => formStore.isActive.setValue(true)}
                    onClickNo={() => formStore.isActive.setValue(false)}
                  />
                }
              />
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  )
})
